import type { RunwayPoint } from './types'

export type Matrix = [number, number, number, number, number, number]

export const IDENTITY: Matrix = [1, 0, 0, 1, 0, 0]

export const multiply = (
    [a1, b1, c1, d1, e1, f1]: Matrix,
    [a2, b2, c2, d2, e2, f2]: Matrix,
): Matrix => [
    a1 * a2 + c1 * b2,
    b1 * a2 + d1 * b2,
    a1 * c2 + c1 * d2,
    b1 * c2 + d1 * d2,
    a1 * e2 + c1 * f2 + e1,
    b1 * e2 + d1 * f2 + f1,
]

export const compose = (...matrices: Matrix[]): Matrix => matrices.reduce(multiply, IDENTITY)

export const translation = (x: number, y: number): Matrix => [1, 0, 0, 1, x, y]

export const scaling = (x: number, y: number): Matrix => [x, 0, 0, y, 0, 0]

export const rotation = (radians: number): Matrix => {
    const cos = Math.cos(radians)
    const sin = Math.sin(radians)
    return [cos, sin, -sin, cos, 0, 0]
}

export const applyMatrix = ([a, b, c, d, e, f]: Matrix, { x, y }: RunwayPoint): RunwayPoint => ({
    x: a * x + c * y + e,
    y: b * x + d * y + f,
})

const NUMBER = /[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g

const transformFunction = (name: string, args: number[]): Matrix => {
    const [first = 0, second, third] = args
    switch (name) {
        case 'translate':
            return translation(first, second ?? 0)
        case 'scale':
            return scaling(first, second ?? first)
        case 'rotate': {
            const turn = rotation((first * Math.PI) / 180)
            return second === undefined
                ? turn
                : compose(
                      translation(second, third ?? 0),
                      turn,
                      translation(-second, -(third ?? 0)),
                  )
        }
        case 'matrix':
            return args.length === 6 ? (args as Matrix) : IDENTITY
        default:
            return IDENTITY
    }
}

export const parseTransform = (transform: string | null | undefined): Matrix => {
    const calls: string[] = (transform ?? '').match(/\w+\s*\([^)]*\)/g) ?? []
    return calls.reduce((matrix: Matrix, call: string): Matrix => {
        const name = call.slice(0, call.indexOf('(')).trim()
        const args = (call.slice(call.indexOf('(')).match(NUMBER) ?? []).map(Number)
        return multiply(matrix, transformFunction(name, args))
    }, IDENTITY)
}

type AbsoluteCommand = 'M' | 'L' | 'C' | 'Q' | 'A' | 'Z'

interface Segment {
    command: AbsoluteCommand
    values: number[]
}

const PARAMETER_COUNT: Record<string, number> = {
    M: 2,
    L: 2,
    H: 1,
    V: 1,
    C: 6,
    S: 4,
    Q: 4,
    T: 2,
    A: 7,
    Z: 0,
}

const tokenize = (path: string) =>
    path.match(/[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) ?? []

export const parsePath = (path: string): Segment[] => {
    const tokens = tokenize(path)
    const segments: Segment[] = []
    let x = 0
    let y = 0
    let startX = 0
    let startY = 0
    let lastControl: RunwayPoint | undefined
    let lastCommand = ''
    let index = 0
    let command = ''

    while (index < tokens.length) {
        if (/[a-z]/i.test(tokens[index])) {
            command = tokens[index]
            index += 1
        } else if (command === 'M') {
            command = 'L'
        } else if (command === 'm') {
            command = 'l'
        }
        const upper = command.toUpperCase()
        const count = PARAMETER_COUNT[upper]
        if (count === undefined) {
            throw new Error(`Unsupported path command "${command}".`)
        }
        const values = tokens.slice(index, index + count).map(Number)
        if (values.length < count || values.some(value => Number.isNaN(value))) {
            throw new Error('The path data is incomplete.')
        }
        index += count
        const relative = command !== upper
        const dx = relative ? x : 0
        const dy = relative ? y : 0
        const reflected = () => ({
            x: lastControl ? 2 * x - lastControl.x : x,
            y: lastControl ? 2 * y - lastControl.y : y,
        })

        switch (upper) {
            case 'M':
                x = values[0] + dx
                y = values[1] + dy
                startX = x
                startY = y
                segments.push({ command: 'M', values: [x, y] })
                lastControl = undefined
                break
            case 'L':
            case 'H':
            case 'V':
                x = upper === 'V' ? x : values[0] + dx
                y = upper === 'H' ? y : values[upper === 'V' ? 0 : 1] + dy
                segments.push({ command: 'L', values: [x, y] })
                lastControl = undefined
                break
            case 'C':
            case 'S': {
                const first =
                    upper === 'C'
                        ? { x: values[0] + dx, y: values[1] + dy }
                        : /[CcSs]/.test(lastCommand)
                          ? reflected()
                          : { x, y }
                const rest = upper === 'C' ? values.slice(2) : values
                const second = { x: rest[0] + dx, y: rest[1] + dy }
                x = rest[2] + dx
                y = rest[3] + dy
                segments.push({
                    command: 'C',
                    values: [first.x, first.y, second.x, second.y, x, y],
                })
                lastControl = second
                break
            }
            case 'Q':
            case 'T': {
                const control =
                    upper === 'Q'
                        ? { x: values[0] + dx, y: values[1] + dy }
                        : /[QqTt]/.test(lastCommand)
                          ? reflected()
                          : { x, y }
                const end = upper === 'Q' ? values.slice(2) : values
                x = end[0] + dx
                y = end[1] + dy
                segments.push({ command: 'Q', values: [control.x, control.y, x, y] })
                lastControl = control
                break
            }
            case 'A':
                x = values[5] + dx
                y = values[6] + dy
                segments.push({ command: 'A', values: [...values.slice(0, 5), x, y] })
                lastControl = undefined
                break
            case 'Z':
                x = startX
                y = startY
                segments.push({ command: 'Z', values: [] })
                lastControl = undefined
                break
        }
        lastCommand = command
    }
    return segments
}

const pointsOf = (segment: Segment): RunwayPoint[] => {
    const { command, values } = segment
    if (command === 'A') {
        return [{ x: values[5], y: values[6] }]
    }
    const points: RunwayPoint[] = []
    for (let index = 0; index + 1 < values.length; index += 2) {
        points.push({ x: values[index], y: values[index + 1] })
    }
    return points
}

export const pathPoints = (path: string): RunwayPoint[] => parsePath(path).flatMap(pointsOf)

const formatNumber = (value: number) => {
    const rounded = Math.round(value * 100) / 100
    return Object.is(rounded, -0) ? '0' : String(rounded)
}

const transformArc = (matrix: Matrix, values: number[]): number[] => {
    const [a, b, c, d] = matrix
    const determinant = a * d - b * c
    const scale = Math.sqrt(Math.abs(determinant))
    const angle = (Math.atan2(b, a) * 180) / Math.PI
    const end = applyMatrix(matrix, { x: values[5], y: values[6] })
    const sweep = determinant < 0 ? 1 - values[4] : values[4]
    return [values[0] * scale, values[1] * scale, values[2] + angle, values[3], sweep, end.x, end.y]
}

export const serializePath = (segments: Segment[]): string =>
    segments
        .map(({ command, values }) =>
            command === 'Z' ? 'Z' : `${command}${values.map(formatNumber).join(' ')}`,
        )
        .join(' ')

export const transformPath = (path: string, matrix: Matrix): string =>
    serializePath(
        parsePath(path).map(segment =>
            segment.command === 'A'
                ? { ...segment, values: transformArc(matrix, segment.values) }
                : {
                      ...segment,
                      values: pointsOf(segment).flatMap(point => {
                          const moved = applyMatrix(matrix, point)
                          return [moved.x, moved.y]
                      }),
                  },
        ),
    )
